<template>
  <div style="background-color: white">
    <section
      aria-label="Introduction"
      class="section-padding mx-sm-4 mx-1 pb-0"
    >
      <h1
        class="text-center mt-16 mx-auto alt-h1 font-weight-black"
        v-html="$t('plans.tagline')"
      ></h1>
    </section>
    <v-container fluid class="max-width-container">
      <v-row justify="end">
        <v-col cols="12" md="4" class="text-right">
          <p class="mb-2">{{ $t('plans.save', { percentage: 14 }) }}</p>
          <v-btn-toggle v-model="billed" mandatory class="toggle-button">
            <v-btn :value="$t('plans.monthly')" depressed>
              {{ $t('plans.monthly') }}
            </v-btn>
            <v-btn :value="$t('plans.annual')" depressed>
              {{ $t('plans.annual') }}
            </v-btn>
          </v-btn-toggle>
        </v-col>
      </v-row>

      <!-- Card Section -->
      <v-row class="d-flex justify-center">
        <v-col
          cols="12"
          md="3"
          class="d-flex pa-0"
          v-for="(plan, index) in plans"
          :key="index"
        >
          <v-card
            class="card-styled gradient-border"
            outlined
            style="width: 100%"
          >
            <v-card-title class="justify-center pt-10">
              <h2 class="text-h5 font-weight-black">{{ plan.title }}</h2>
            </v-card-title>
            <v-card-text class="text-center">
              <div class="alt-h2 gradient-text-blue-purple font-weight-black">
                {{
                  billed === $t('plans.annual')
                    ? plan.priceYearly
                    : plan.priceMonthly
                }}
                <span
                  v-if="plan.priceMonthly !== $t('plans.customPricing')"
                  class="price-inline-month"
                >
                  / {{ $t('plans.month') }}
                </span>
              </div>
              <div class="mb-5">
                {{
                  billed === $t('plans.annual')
                    ? plan.priceDetailYearly
                    : plan.priceDetailMonthly
                }}
              </div>
              <v-chip
                v-if="plan.chipText"
                class="mx-auto mt-2"
                small
                color="light"
                outlined
              >
                <v-icon left>
                  {{ icons.mdiAccount }}
                </v-icon>
                {{ plan.chipText }}
              </v-chip>
            </v-card-text>
            <v-card-actions class="justify-center pb-10">
              <v-btn
                class="px-4"
                dark
                :color="plan.buttonColor"
                :class="plan.buttonClass"
                :to="
                  billed === $t('plans.annual')
                    ? localePath(plan.linkYearly)
                    : localePath(plan.linkMonthly)
                "
                large
              >
                {{ plan.buttonText }}
              </v-btn>
            </v-card-actions>
            <v-btn v-if="index > 0" class="floating-btn" icon>
              <v-icon color="primary">{{ icons.mdiCrown }}</v-icon>
            </v-btn>
          </v-card>
        </v-col>
      </v-row>

      <!-- Table Section -->
      <v-row>
        <v-col cols="12">
          <v-simple-table>
            <thead>
              <tr class="header-row">
                <th class="text-left text-md-h6">{{ $t('links.features') }}</th>
                <th class="text-left text-md-h6">{{ $t('plans.free') }}</th>
                <th class="text-left text-md-h6">
                  {{ $t('plans.hobbyPlan') }}
                </th>
                <th class="text-left text-md-h6">
                  {{ $t('plans.educatorPlan') }}
                </th>
                <th class="text-left text-md-h6">
                  {{ $t('plans.schoolPlan') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(feature, index) in features"
                :key="index"
                :class="index % 2 === 0 ? 'even-row' : 'odd-row'"
              >
                <td>{{ feature.name }}</td>
                <td>{{ feature.free }}</td>
                <td>{{ feature.educator }}</td>
                <td>{{ feature.plus }}</td>
                <td>{{ feature.school }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-col>
      </v-row>

      <div class="text-center mt-10">
        <p>
          <strong>{{ $t('plans.message.automaticRenew') }}</strong>
        </p>
      </div>
    </v-container>
    <questions-content :faq="faq" />
    <section
      class="section-padding"
      style="background-color: rgb(234, 243, 253)"
    >
      <v-container class="px-10 mx-auto" style="max-width: 1440px">
        <h2
          class="alt-h2 gradient-text-blue font-weight-black mb-4 text-center"
          style="color: rgb(51, 139, 236)"
        >
          Any more questions?
        </h2>
        <p class="text-center mx-auto mb-8" style="max-width: 800px">
          If you have any questions or would like to discover more about
          bringing Elementari to your classroom, we're here to help.
        </p>
        <div class="text-center">
          <v-btn
            x-large
            color="accent"
            :to="localePath('/contact/')"
            class="d-inline-flex mb-4"
          >
            Contact us
          </v-btn>
          <v-btn
            x-large
            color="primary"
            href="https://calendly.com/elementari/help"
            target="_blank"
            class="d-inline-flex mb-4"
          >
            Book a demo
          </v-btn>
        </div>
      </v-container>
    </section>

    <main-footer />
  </div>
</template>

<script>
  import { mdiCrown, mdiAccount } from '@mdi/js'
  import { computed } from 'vue'
  export default {
    components: {
      'questions-content': () =>
        import('~/components/utils/QuestionsContent.vue'),
      'main-footer': () => import('~/components/MainFooter.vue')
    },
    head() {
      return {
        title: this.$t('meta.plans.title'),
        meta: [
          // hid is used as unique identifier. Do not use `vmid` for it as it will not work
          {
            hid: 'description',
            name: 'description',
            content: this.$t('meta.plans.description')
          },

          // Open Graph
          {
            hid: 'og:title',
            name: 'og:title',
            content: this.$t('meta.plans.title')
          },
          {
            hid: 'og:description',
            name: 'og:description',
            content: this.$t('meta.plans.description')
          },
          {
          hid: 'og:url',
          property: 'og:url',
          content: 'https://elementari.com' + this.$route.path
        }
        ],
      }
    },
    data() {
      return {
        billed: this.$t('plans.annual'), // default selection
        icons: {
          mdiCrown,
          mdiAccount
        },
        features: [
          {
            name: this.$t('plans.feature.createStudentAccounts.title'),
            free: '35',
            educator: '35',
            plus: '150',
            school: '150+'
          },
          {
            name: this.$t('plans.feature.createClassrooms.title'),
            free: '1',
            educator: '1',
            plus: this.$t('plans.unlimited'),
            school: this.$t('plans.unlimited')
          },
          {
            name: this.$t('plans.feature.unlimitedProjects.title'),
            free: '✔',
            educator: '✔',
            plus: '✔',
            school: '✔'
          },
          {
            name: this.$t('plans.feature.accessLessons'),
            free: this.$t('plans.limited'),
            educator: this.$t('plans.unlimited'),
            plus: this.$t('plans.unlimited'),
            school: this.$t('plans.unlimited')
          },
          {
            name: this.$t('plans.feature.accessToAssetLibrary.title'),
            free: this.$t('plans.limited'),
            educator: this.$t('plans.unlimited'),
            plus: this.$t('plans.unlimited'),
            school: this.$t('plans.unlimited')
          },
          {
            name: this.$t('plans.feature.uploadAssets'),
            free: '—',
            educator: '✔',
            plus: '✔',
            school: '✔'
          },
          {
            name: this.$t('plans.feature.createStudentFeedback.title'),
            free: '—',
            educator: '✔',
            plus: '✔',
            school: '✔'
          },
          {
            name: this.$t('plans.feature.studentAnalytics.title'),
            free: '—',
            educator: '✔',
            plus: '✔',
            school: '✔'
          },
          {
            name: this.$t('plans.feature.createTeacherAccounts'),
            free: '—',
            educator: '—',
            plus: '—',
            school: '✔'
          },
          {
            name: this.$t('plans.feature.prioritySupport'),
            free: '—',
            educator: '—',
            plus: '—',
            school: '✔'
          },
          {
            name: this.$t('plans.feature.training'),
            free: 'Basic',
            educator: 'Basic',
            plus: 'Premium',
            school: 'Premium'
          }
        ],
        faq: [
          {
            question: 'Do you accept Purchase Orders?',
            answer:
              'Yes, we do for school / district purchases. You can <a href="https://elementari.com/quote/">generate an instant quote</a> or <a href="mailto:support@elementari.com">contact us</a>.'
          },
          {
            question: 'Where can I download the Elementari W-9 form?',
            answer:
              'You can download the W-9 form <a href="https://elementari.com/files/Elementari_W9.pdf">here</a>.'
          },
          {
            question: 'What is the cost per student for Elementari?',
            answer:
              'There is a cost per student after 150 students at $2 per student. Use our <a href="https://elementari.com/quote/">instant quote calculator</a> to generate a quote or email us at <a href="mailto:support@elementari.com">support@elementari.com</a>.'
          },
          {
            question: 'What should I do if I exceed my quota of students?',
            answer:
              'You can remove students from the class or archive the classroom. When the classroom is archived, the students will no longer count towards the student limit.'
          },
          {
            question: 'Is there an iPad app for Elementari?',
            answer:
              'There is no Elementari app on the App Store. However, you can use Elementari on the iPad and save the website to your home screen to easily access it in one click.'
          },
          {
            question: 'Is there a free trial available for Elementari?',
            answer:
              'Yes, Sign up for our Educator plan for a 14-day free trial or for our Educator Plus plan for 30-day free trial. Elementari also has a forever free plan!'
          },
          {
            question: 'Do you offer any discounts?',
            answer:
              'We offer discounts for bulk purchases. <a href="https://elementari.com/quote/">Generate an instant quote</a> for more details.'
          },
          {
            question: 'What support is available?',
            answer:
              'Contact us at <a href="mailto:support@elementari.com">support@elementari.com</a> for assistance, view our <a href="https://help.elementari.com/" target="_blank">Help Center</a>, or give us a call at 480-955-0101.'
          }
        ]
      }
    },
    computed: {
      plans() {
        const plans =  [
          {
            title: this.$t('plans.free'),
            priceMonthly: this.$t('plans.price', {amount: 0 }),
            priceYearly: this.$t('plans.price', {amount: 0 }),
            priceDetailMonthly: this.$t('plans.foreverFree'),
            priceDetailYearly: this.$t('plans.foreverFree'),
            chipText: this.$tc('classroom.student', 35),
            buttonText: this.$t('plans.startNow'),
            buttonClass: 'text-no-wrap',
            linkMonthly: this.localePath('/getting-started/'),
            linkYearly: this.localePath('/getting-started/')
          },
          {
            title: this.$t('plans.hobbyPlan'),
            priceMonthly: this.$t('plans.price', {amount: 10 }),
            priceYearly: this.$t('plans.price', {amount: 8 }),
            priceDetailMonthly: this.$t('plans.billedMonthly'),
            priceDetailYearly: this.$t('plans.billedYearly'),
            chipText: this.$tc('classroom.student',35),
            buttonText: this.$t('plans.getPlan'),
            buttonColor: '#ff883e',
            buttonClass: 'text-no-wrap',
            linkMonthly: this.localePath('/plans/educator_monthly/'),
            linkYearly: this.localePath('/plans/educator_yearly/')
          },
          {
            title: this.$t('plans.educatorPlan'),
            priceMonthly: this.$t('plans.price', {amount: 29 }),
            priceYearly: this.$t('plans.price', {amount: 25 }),
            priceDetailMonthly: this.$t('plans.billedMonthly'),
            priceDetailYearly: this.$t('plans.billedYearly'),
            chipText: this.$tc('classroom.student', 150),
            buttonText: this.$t('plans.getPlan'),
            buttonColor: '#ff883e',
            buttonClass: 'text-no-wrap',
            linkMonthly: this.localePath('/plans/educator_plus_monthly/'),
            linkYearly: this.localePath('/plans/educator_plus_yearly/')
          },
          {
            title: this.$t('plans.schoolPlan'),
            priceMonthly: this.$t('plans.customPricing'),
            priceYearly: this.$t('plans.customPricing'),
            priceDetailMonthly: this.$t('plans.contactUs'),
            priceDetailYearly: this.$t('plans.studentAccountsPricePerYear'),
            chipText: this.$tc('classroom.student', '150+'),
            buttonText: this.$t('plans.getInstantQuote'),
            buttonColor: '#ff883e',
            buttonClass: 'text-no-wrap',
            linkMonthly: this.localePath('/quote/'),
            linkYearly: this.localePath('/quote/')
          }
        ]
        return plans
      }
    }
  }
</script>

<style scoped>
  .toggle-button {
    border: 1px solid #e0e0e0;
    border-radius: 30px;
  }

  .v-btn--active {
    background-color: #ff883e !important;
    color: white !important;
  }
  .plans-color {
    background: #1488cc;
    background: -webkit-linear-gradient(to right, #2b32b2, #1488cc);
    background: linear-gradient(to right, #2b32b2, #1488cc);
  }
  .price-inline-month {
    font-size: 1rem;
    margin-left: 3px;
  }
  .v-card__subtitle,
  .v-card__text {
    line-height: normal;
  }
  .max-width-container {
    max-width: 1260px;
    margin: 0 auto;
  }
  .responsive-img {
    max-width: 100%;
    height: auto;
  }
  .display-1 {
    font-size: 2.5rem;
  }
  .v-card {
    margin: 10px;
    position: relative;
  }
  .card-styled {
    border: 2px solid #174694;
    color: #174694;
  }
  .header-row {
    background-color: #000 !important;
    height: 60px;
  }
  .header-row th {
    color: #fff !important;
    font-weight: bold;
    font-size: 18px;
  }
  .even-row {
    background-color: #fff;
  }
  .odd-row {
    background-color: #f5f5f5;
  }
  .floating-btn {
    position: absolute;
    top: 10px;
    right: 10px;
    background-color: #fff;
    border-radius: 50%;
    padding: 5px;
  }
</style>
